import {
    BarChart3,
    LineChart as LineIcon,
    PieChart as PieIcon,
    Table as TableIcon,
    ArrowLeftRight,
    FileText,
    Hash,
    Trophy,
    Globe,
    Lock,
    Users,
} from "lucide-react";
import { Routes } from "~/routes";
import {
    ChartConfigByType,
    ChartType,
    ChartVisibility,
} from "~/modules/charts/types";

// // // //

type Meta = {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
};

type TypeMeta = Meta & {
    description: string;
};

/**
 * TYPE_META
 * Icon, label, and description for each chart visualization type.
 */
export const TYPE_META: Record<ChartType, TypeMeta> = {
    timeseries: {
        icon: LineIcon,
        label: "Timeseries",
        description: "Trends over time",
    },
    bar_chart: {
        icon: BarChart3,
        label: "Bar chart",
        description: "Compare entities",
    },
    composition: {
        icon: PieIcon,
        label: "Composition",
        description: "Share of a whole",
    },
    asset_table: {
        icon: TableIcon,
        label: "Asset table",
        description: "Rows of asset records",
    },
    metric: {
        icon: Hash,
        label: "Metric",
        description: "One headline number",
    },
    net_flows: {
        icon: ArrowLeftRight,
        label: "Net flows",
        description: "Token Mints vs. Burns",
    },
    league_table: {
        icon: Trophy,
        label: "League table",
        description: "Ranked Leaderboard",
    },
    fact_sheet: {
        icon: FileText,
        label: "Fact sheet",
        description: "Key facts at a glance",
    },
};

type VisibilityMeta = Meta & {
    /** Tailwind classes for the small pill shown on chart cards. */
    badgeClassName: string;
};

/**
 * VISIBILITY_META
 * Icon, label, and card-pill styling for each chart visibility setting.
 */
export const VISIBILITY_META: Record<ChartVisibility, VisibilityMeta> = {
    public: {
        icon: Globe,
        label: "Community",
        badgeClassName: "bg-emerald-100 text-emerald-700",
    },
    private: {
        icon: Lock,
        label: "Private",
        badgeClassName: "bg-muted text-foreground",
    },
    organization: {
        icon: Users,
        label: "Team",
        badgeClassName: "bg-blue-100 text-blue-700",
    },
};

/**
 * CHART_TYPE_TO_EXPLORE_ROUTE
 * The chart types that have a dedicated `/charts/explore/*` page. Other chart
 * types (metric, fact_sheet) have no explore destination and are
 * intentionally absent.
 */
export const CHART_TYPE_TO_EXPLORE_ROUTE: Record<
    keyof ChartConfigByType,
    Routes
> = {
    timeseries: Routes.exploreTimeseries,
    bar_chart: Routes.exploreBarChart,
    composition: Routes.exploreComposition,
    net_flows: Routes.exploreNetFlows,
    league_table: Routes.exploreLeagueTable,
    asset_table: Routes.exploreAssetTable,
};
